import React, { useState, useContext, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { FormGroup, Form, Input, Label, Button } from "reactstrap";
import { toast } from "react-toastify";

import UserContext from "../auth/UserContext";
import ValetApi from "../api/Api";
import DamagesForm from "./DamagesForm";
import TicketNumberScannerInput from "../components/TicketNumberScannerInput";
import { digitsOnly, formatPhoneDisplay } from "../utility/formatPhoneDisplay";
import { VEHICLE_COLOR_OPTIONS, VEHICLE_MAKE_OPTIONS } from "../constants/vehicleMetaOptions";
import styles from "./NewAddVehicleForm.module.css";

const REQUIRED_FIELDS = new Set(["ticketNum", "mobile", "color", "make"]);

const NewAddVehicleForm = () => {
    const { startVehicleCheckIn, completeVehicleCheckIn } = useContext(UserContext);
    const history = useHistory();
    const isMountedRef = useRef(false);
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);
    const [showDamagesForm, setShowDamagesForm] = useState(false); //state to manage navigation between forms
    const [damagePositions, setDamagePositions] = useState([]); // state to store damage positions
    const [damagePhotos, setDamagePhotos] = useState([]);
    const [checkInStep, setCheckInStep] = useState(1);
    const [createdVehicleId, setCreatedVehicleId] = useState(null);
    const [checkInTransactionId, setCheckInTransactionId] = useState(null);
    const [isStartingCheckIn, setIsStartingCheckIn] = useState(false);
    const [isCompletingCheckIn, setIsCompletingCheckIn] = useState(false);
    const INITIAL_STATE = {
        ticketNum: "",
        statusId: "3",
        mobile: "",
        color: "",
        make: "",
        damages: "",
        notes: "",
    };
    const [formData, setFormData] = useState(INITIAL_STATE);

    // sms consent for registration
    // const [smsConsent, setSmsConsent] = useState(false);

    const handleDamagesSubmit = (payload) => {
        const positions = Array.isArray(payload) ? payload : payload?.positions || [];
        const photos = Array.isArray(payload?.photos) ? payload.photos : [];

        setDamagePositions(positions); // save the damage positions
        setDamagePhotos(photos);
        setShowDamagesForm(false); // return to the AddVehicleForm
    };

    const handleDamagesClick = () => {
        // Reopen with the current in-progress marks so valets can adjust before saving.
        setShowDamagesForm(true); // show the damages form
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        const normalizedValue = name === "mobile" ? digitsOnly(value).slice(0, 10) : value;

        setFormData((formData) => ({
            ...formData,
            [name]: normalizedValue,
        }));
    };

    const handleStepOneSubmit = async (e) => {
        e.preventDefault();
        if (isStartingCheckIn) return;

        const cleanedTicket = String(formData.ticketNum || "").replace(/\D/g, "");
        const ticketNum = Number(cleanedTicket);

        if (!Number.isInteger(ticketNum) || ticketNum <= 0) {
            toast.error("Ticket Number required and must be greater than 0.", { autoClose: 1000 });
            return;
        }

        const normalizedMobile = digitsOnly(formData.mobile).slice(0, 10);
        if (normalizedMobile.length < 10) {
            toast.error("Customer phone number is required.", { autoClose: 1000 });
            return;
        }

        try {
            setIsStartingCheckIn(true);
            const result = await startVehicleCheckIn(ticketNum, normalizedMobile);
            if (!isMountedRef.current || !result?.success) return;

            setCreatedVehicleId(result.vehicleId);
            setCheckInTransactionId(result.checkInTransactionId || null);
            setCheckInStep(2);
        } catch (err) {
            if (isMountedRef.current) toast.error(err?.[0] || err?.response?.data?.error || err?.message || "Error starting check-in", { autoClose: 1000 });
        } finally {
            if (isMountedRef.current) setIsStartingCheckIn(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isCompletingCheckIn) return;

        if (!createdVehicleId) {
            toast.error("Step 1 is required before completing check-in.", { autoClose: 1000 });
            return;
        }

        if (!String(formData.color || "").trim()) {
            toast.error("Vehicle color is required.", { autoClose: 1000 });
            return;
        }

        if (!String(formData.make || "").trim()) {
            toast.error("Vehicle make is required.", { autoClose: 1000 });
            return;
        }

        try {
            setIsCompletingCheckIn(true);

            const hasDamageEvidence = damagePositions.length > 0 || damagePhotos.length > 0;
            const hasValidCheckInTransaction = Number.isInteger(Number(checkInTransactionId)) && Number(checkInTransactionId) > 0;

            if (hasDamageEvidence && !hasValidCheckInTransaction) {
                toast.error("Damage evidence could not be linked to this visit. Restart check-in.", { autoClose: 1200 });
                return;
            }

            const shouldPersistDamageAssessment = hasValidCheckInTransaction && hasDamageEvidence;

            if (shouldPersistDamageAssessment) {
                await ValetApi.saveTransactionDamages(Number(checkInTransactionId), damagePositions, formData.notes, damagePhotos, { responseMode: "minimal" });
                if (!isMountedRef.current) return;
            }

            const result = await completeVehicleCheckIn(createdVehicleId, formData.statusId, formData.color, formData.make, JSON.stringify(damagePositions), formData.notes);

            if (!isMountedRef.current || !result?.success) return;

            setFormData(INITIAL_STATE);
            setDamagePositions([]);
            setDamagePhotos([]);
            setShowDamagesForm(false);
            setCreatedVehicleId(null);
            setCheckInTransactionId(null);
            setCheckInStep(1);
            history.push("/valetDashBoard");
        } catch (err) {
            if (isMountedRef.current) toast.error(err?.[0] || err?.response?.data?.error || err?.message || "Error completing check-in", { autoClose: 1000 });
        } finally {
            if (isMountedRef.current) setIsCompletingCheckIn(false);
        }
    };

    // Patch replaces handleSubmit with this to bypass ticket number validation.
    //     e.preventDefault();
    //     try {
    // addVehicle(formData.ticketNum, formData.statusId, formData.mobile, formData.color, formData.make, JSON.stringify(damagePositions), formData.notes);
    //         toast.success("Vehicle Added", { autoClose: 1000 });
    //         setFormData(INITIAL_STATE);
    //         history.push(`/valetDashBoard`);
    //     } catch (err) {
    //         toast.error(err?.response?.data?.error || err?.message || "Error adding vehicle", { autoClose: 1000 });
    //     }
    // };

    const statusOptions = [
        // { value: 1, label: "checked_in" },
        { value: 2, label: "staged" },
        { value: 3, label: "parked" },
        // { value: 4, label: "requested" },
        // { value: 5, label: "retrieved" },
        // { value: 6, label: "checked_out" },
    ];

    const renderDamagesField = (val) => {
        const hasMarkedDamages = damagePositions.length > 0;
        const hasDamagePhotos = damagePhotos.length > 0;
        const hasDamageEvidence = hasMarkedDamages || hasDamagePhotos;
        const shouldLockDamagesField = isCompletingCheckIn;
        const placeholder = hasMarkedDamages || hasDamagePhotos ? `${damagePositions.length} mark${damagePositions.length === 1 ? "" : "s"} • ${damagePhotos.length} photo${damagePhotos.length === 1 ? "" : "s"}` : "Click to add damages";
        const className = `${styles.damagesField} ${hasDamageEvidence ? styles.damagesComplete : styles.damagesIncomplete} ${shouldLockDamagesField ? styles.damagesDisabled : ""}`;

        return (
            <Input
                id={val}
                type="text"
                name={val}
                value={formData[val]}
                onClick={handleDamagesClick}
                placeholder={placeholder}
                readOnly
                disabled={shouldLockDamagesField}
                className={className}></Input>
        );
    };

    const renderColorField = (val) => (
        <Input
            id={val}
            type="select"
            name={val}
            value={formData[val]}
            onChange={handleChange}>
            <option value="">{val}</option>
            {VEHICLE_COLOR_OPTIONS.map((color, idx) => (
                <option
                    key={idx}
                    value={color.toLowerCase()}>
                    {color}
                </option>
            ))}
        </Input>
    );

    const renderStatusField = (val) => (
        <Input
            id={val}
            type="select"
            name={val}
            value={formData[val]}
            onChange={handleChange}
            // disabled
            className={formData.statusId === "1" ? styles.statusCheckedIn : ""}>
            {statusOptions.map((status, idx) => (
                <option
                    key={idx}
                    value={status.value}>
                    {status.label}
                </option>
            ))}
        </Input>
    );

    const renderMakeField = (val) => (
        <Input
            id={val}
            type="select"
            name={val}
            value={formData[val]}
            onChange={handleChange}>
            <option value="">{val}</option>
            {VEHICLE_MAKE_OPTIONS.map((make, idx) => (
                <option
                    key={idx}
                    value={make}>
                    {make}
                </option>
            ))}
        </Input>
    );

    const renderNumberField = (val) => (
        <Input
            id={val}
            type="tel"
            inputMode="numeric"
            pattern="[0-9]*"
            name={val}
            placeholder={val.toLowerCase()}
            value={formData[val]}
            onChange={handleChange}
        />
    );

    const renderMobileField = (val) => (
        <Input
            id={val}
            type="tel"
            inputMode="tel"
            name={val}
            placeholder="(555) 123-4567"
            value={formatPhoneDisplay(formData[val])}
            onChange={handleChange}
        />
    );

    // const renderMobileField = (val) => (
    //     <>
    //         <Input
    //             id={val}
    //             type="tel"
    //             inputMode="numeric"
    //             pattern="[0-9]*"
    //             name={val}
    //             placeholder={val.toLowerCase()}
    //             value={formData[val]}
    //             onChange={handleChange}
    //         />
    //         <div style={{ marginTop: "8px", fontSize: "0.85rem", lineHeight: 1.35 }}>
    //             By providing a mobile number, customer agrees to receive transactional valet SMS. One message per check-in. Message and data rates may apply. Reply STOP to opt out. Reply HELP for help. <Link to="/terms">Terms</Link> | <Link to="/privacy-sms-policy">Privacy</Link>
    //         </div>
    //         <label style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "8px", fontSize: "0.85rem" }}>
    //             <input
    //                 type="checkbox"
    //                 checked={smsConsent}
    //                 onChange={(e) => setSmsConsent(e.target.checked)}
    //             />
    //             Customer consent confirmed
    //         </label>
    //     </>
    // );

    const renderDefaultField = (val) => (
        <Input
            id={val}
            type={val !== "email" ? "text" : "email"}
            name={val}
            placeholder={val.toLowerCase()}
            value={formData[val]}
            onChange={handleChange}
        />
    );

    const renderTicketField = (val) => (
        <TicketNumberScannerInput
            id={val}
            name={val}
            value={formData[val]}
            onChange={handleChange}
            onDetected={(ticketNum) => setFormData((prev) => ({ ...prev, ticketNum }))}
            autoFocusOnMount={true}
            autoStartScan={false}
        />
    );

    const renderInputField = (val) => {
        switch (val) {
            case "color":
                return renderColorField(val);
            case "make":
                return renderMakeField(val);
            case "ticketNum":
                return renderTicketField(val);
            case "mobile":
                return renderMobileField(val);
            case "damages":
                return renderDamagesField(val);
            case "statusId":
                return renderStatusField(val);
            default:
                return renderDefaultField(val);
        }
    };

    const stepOneFields = ["ticketNum", "mobile"];
    const stepTwoFields = ["color", "make", "statusId", "damages", "notes"];

    return showDamagesForm ? (
        <DamagesForm
            onSubmit={handleDamagesSubmit}
            initialDamagePositions={damagePositions}
            initialDamagePhotos={damagePhotos}
        />
    ) : (
        <div className={styles.addVehicleFormWrapper}>
            <Form
                onSubmit={checkInStep === 1 ? handleStepOneSubmit : handleSubmit}
                className={styles.addVehicleForm}>
                <h3 className={styles.addVehicleTitle}>Add Vehicle</h3>
                <div className={styles.checkInStepMeta}>{checkInStep === 1 ? "Step 1 of 2: Ticket + phone" : "Step 2 of 2: Vehicle details + damage assessment"}</div>

                {checkInStep === 2 ? (
                    <div className={styles.checkInStepSummary}>
                        Ticket {formData.ticketNum} • {formatPhoneDisplay(formData.mobile)}
                    </div>
                ) : null}

                {(checkInStep === 1 ? stepOneFields : stepTwoFields).map((val, idx) => (
                    <FormGroup key={`FormGroup-${val}`}>
                        {renderInputField(val)}
                        {REQUIRED_FIELDS.has(val) ? <div className={styles.requiredInlineHint}>Required</div> : null}
                        <Label
                            htmlFor={val}
                            key={`Label-${idx}`}></Label>
                    </FormGroup>
                ))}
                <Button
                    className={styles.addVehicleBtn}
                    color="primary"
                    disabled={checkInStep === 1 ? isStartingCheckIn : isCompletingCheckIn}
                    block>
                    {checkInStep === 1 ? (isStartingCheckIn ? "Sending..." : "Continue to Step 2") : isCompletingCheckIn ? "Saving..." : "Complete Check-In"}
                </Button>
            </Form>
        </div>
    );
};

export default NewAddVehicleForm;
